import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { TestTube, UploadSimple, FileText, Download } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { fmtDate as fmtDatePt } from '@/utils/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AnalisesClientePage() {
  const [requests, setRequests] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [linkedRequestId, setLinkedRequestId] = useState('');
  const [uploadNotes, setUploadNotes] = useState('');

  const fetchAll = useCallback(async () => {
    try {
      const [rq, rs] = await Promise.all([
        axios.get(`${API}/api/my-lab-requests`),
        axios.get(`${API}/api/my-lab-results`),
      ]);
      setRequests(Array.isArray(rq.data) ? rq.data : []);
      setResults(Array.isArray(rs.data) ? rs.data : []);
    } catch { toast.error('Erro a carregar'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const downloadRequestPdf = async (req) => {
    try {
      const { data } = await axios.get(`${API}/api/lab-requests/${req.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `pedido_exames_${(req.created_at || '').slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Erro ao descarregar PDF'); }
  };

  const submitUpload = async () => {
    if (!file) { toast.error('Escolhe um ficheiro'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (linkedRequestId) fd.append('request_id', linkedRequestId);
      fd.append('notes', uploadNotes);
      await axios.post(`${API}/api/my-lab-results/upload`, fd);
      toast.success('Resultado enviado à tua nutricionista');
      setShowUpload(false);
      setFile(null);
      setLinkedRequestId('');
      setUploadNotes('');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao enviar'); }
    finally { setUploading(false); }
  };

  return (
    <div className="fade-in max-w-xl mx-auto" data-testid="analises-client-page">
      <div className="flex items-start justify-between mb-1">
        <h1 className="font-heading text-2xl text-brand-text">Análises</h1>
        <button onClick={() => setShowUpload(true)} className="bg-brand-green text-white rounded-full px-3 py-1.5 text-xs font-medium flex items-center gap-1" data-testid="analises-client-upload-btn">
          <UploadSimple size={12} /> Enviar resultado
        </button>
      </div>
      <p className="text-xs text-brand-muted mb-5">Pedidos de exames da tua nutricionista e os teus resultados.</p>

      {loading ? (
        <p className="text-xs text-brand-muted text-center py-10">A carregar...</p>
      ) : (
        <>
          <p className="text-[11px] font-medium text-brand-muted mb-2 uppercase tracking-wide">Pedidos de exames</p>
          {requests.length === 0 ? (
            <div className="bg-white border border-brand-border rounded-2xl p-6 text-center mb-5" data-testid="analises-client-requests-empty">
              <TestTube size={28} className="mx-auto text-brand-placeholder mb-2" weight="duotone" />
              <p className="text-xs text-brand-muted">Ainda sem pedidos de exames.</p>
            </div>
          ) : (
            <ul className="space-y-1.5 mb-5" data-testid="analises-client-requests-list">
              {requests.map(r => (
                <li key={r.id} className="bg-white border border-brand-border rounded-2xl p-3" data-testid={`analises-client-request-${r.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs font-medium text-brand-text">{(r.created_at || '').slice(0, 10)}</p>
                    <button onClick={() => downloadRequestPdf(r)} className="text-[10px] text-brand-green flex items-center gap-1 hover:underline" data-testid={`analises-client-download-${r.id}`}>
                      <Download size={11} /> Descarregar PDF
                    </button>
                  </div>
                  <p className="text-[10px] text-brand-muted">{(r.selected_exams || []).join(', ')}{r.outros_text ? ` · ${r.outros_text}` : ''}</p>
                  {r.notes_paciente && (
                    <p className="text-[10px] text-brand-text italic mt-1.5 bg-brand-bg rounded-xl px-2 py-1.5" data-testid={`analises-client-notes-paciente-${r.id}`}>"{r.notes_paciente}"</p>
                  )}
                </li>
              ))}
            </ul>
          )}

          <p className="text-[11px] font-medium text-brand-muted mb-2 uppercase tracking-wide">Resultados</p>
          {results.length === 0 ? (
            <div className="bg-white border border-brand-border rounded-2xl p-6 text-center" data-testid="analises-client-results-empty">
              <FileText size={28} className="mx-auto text-brand-placeholder mb-2" weight="duotone" />
              <p className="text-xs text-brand-muted">Ainda sem resultados.</p>
            </div>
          ) : (
            <ul className="space-y-1.5" data-testid="analises-client-results-list">
              {results.map(r => {
                const filePath = r.file_path || r.pdf_path;
                return (
                  <li key={r.id} className="bg-white border border-brand-border rounded-2xl p-3" data-testid={`analises-client-result-${r.id}`}>
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-medium text-brand-text">{fmtDatePt(r.date)}</p>
                      {filePath && (
                        <a href={`${API}/api/files/${filePath}`} target="_blank" rel="noreferrer" className="text-[10px] text-brand-green flex items-center gap-1 hover:underline" data-testid={`analises-client-result-download-${r.id}`}>
                          <Download size={11} /> {r.file_name || 'Ficheiro'}
                        </a>
                      )}
                    </div>
                    {r.source === 'patient' && <span className="text-[9px] text-brand-gold-hover">Enviado por ti</span>}
                    {r.notes && <p className="text-[10px] text-brand-muted italic mt-1">"{r.notes}"</p>}
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}

      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="rounded-3xl border-brand-border" data-testid="analises-client-upload-dialog">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Enviar resultado de análises</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Ficheiro (PDF, JPG ou PNG, máx. 15MB)</Label>
              <Input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e => setFile(e.target.files?.[0] || null)} className="rounded-xl border-brand-border" data-testid="analises-client-upload-file" />
            </div>
            {requests.length > 0 && (
              <div>
                <Label className="text-xs text-brand-muted mb-1 block">Associar a um pedido (opcional)</Label>
                <select value={linkedRequestId} onChange={e => setLinkedRequestId(e.target.value)} className="w-full border border-brand-border rounded-xl px-3 py-2 text-sm bg-white" data-testid="analises-client-upload-request-select">
                  <option value="">Nenhum pedido associado</option>
                  {requests.map(r => (
                    <option key={r.id} value={r.id}>{(r.created_at || '').slice(0, 10)} · {(r.selected_exams || []).join(', ').slice(0, 40)}</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Notas (opcional)</Label>
              <Textarea value={uploadNotes} onChange={e => setUploadNotes(e.target.value)} className="rounded-xl border-brand-border text-sm" rows={2} data-testid="analises-client-upload-notes" />
            </div>
            <button onClick={submitUpload} disabled={uploading} className="w-full bg-brand-green text-white rounded-full py-2.5 text-sm font-medium disabled:opacity-50" data-testid="analises-client-upload-submit">{uploading ? 'A enviar...' : 'Enviar'}</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
